// v0.4.86 — Rule-copy coverage diagnostic page.
//
// Catches the next translation drift automatically. Pairs with
// `GET /api/admin/rule-copy-coverage` (routes_rule_copy_coverage.py)
// which enumerates every backend reconciliation rule + page-build
// error code and reports which ones have plain-English copy in
// `lib/ruleCopy.js`.
//
// Pattern signed off 2026-06-19: rather than relying on the
// operator spotting a raw rule code in a screenshot, every new
// backend rule shows up as a `MISSING` row here the moment it
// lands.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { API } from "../lib/api";
import { getToken } from "../lib/authApi";
import { RULE_COPY_NAMES } from "../lib/ruleCopy";


const Badge = ({ ok, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      ok
        ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700"
        : "bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-700"
    }`}
  >
    {ok ? "✓ TRANSLATED" : "⚠ NOT YET TRANSLATED"}
  </span>
);


const TotalsBar = ({ label, total, translated, testId }) => {
  const missing = total - translated;
  const pct = total === 0 ? 100 : Math.round((translated / total) * 100);
  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 text-xs text-slate-700 dark:text-slate-200"
    >
      <span className="font-semibold">{label}</span>
      <span className="tabular-nums">{translated} / {total} ({pct}%)</span>
      {missing > 0 && (
        <span
          data-testid={`${testId}-missing-pill`}
          className="text-rose-700 dark:text-rose-300"
        >
          {/* i18n-ignore */}{missing} missing
        </span>
      )}
    </div>
  );
};


export default function RuleCopyCoverage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/admin/rule-copy-coverage`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        // A2-234 G8 — the server container has no frontend files, so its
        // own reading always said "not translated". The page knows what it
        // ships with: judge each rule against the bundled wording.
        const rules = (j.reconciliation_rules || []).map((x) => ({
          ...x, has_translation: RULE_COPY_NAMES.has(x.name) }));
        const done = rules.filter((x) => x.has_translation).length;
        if (!cancelled) setData({ ...j, reconciliation_rules: rules,
          totals: { ...j.totals, rules_total: rules.length, rules_translated: done,
                    rules_missing: rules.length - done } });
      } catch (e) {
        if (!cancelled) setErr(e?.message || "load failed");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="mb-2 text-xs text-teal-700 dark:text-teal-300">{/* i18n-ignore: developer-only page */}
          / admin / developer tools / rule-copy-coverage
        </div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-50 mb-1">
          Rule-copy coverage <span className="text-slate-400 dark:text-slate-500 text-sm">(developer tool)</span>{/* i18n-ignore: developer-only page */}
        </h1>
        <p className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">{/* i18n-ignore: developer-only page */}
          Developer / maintainer tool — not for daily filing work.
        </p>
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6 max-w-3xl">{/* i18n-ignore: developer-only page */}
          Diagnostic for developers maintaining the rule-translation table. A{" "}
          <span className="font-semibold">NOT YET TRANSLATED</span>{/* i18n-ignore: developer-only page */} row means
          the engine&apos;s rule code has no plain-English label yet —
          operators will see the raw code instead. To fix: add an entry to
          the frontend rule-translation file.
        </p>

        {err && (
          <div
            data-testid="rule-copy-coverage-error"
            className="text-xs text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-700 rounded-md px-3 py-2 mb-4"
          >
            {/* i18n-ignore */}Load failed: {err}
          </div>
        )}

        {data && (
          <>
            <div className="mb-6 flex flex-col gap-2">
              <TotalsBar
                testId="totals-rules"
                label="Reconciliation rules" // i18n-ignore
                total={data.totals.rules_total}
                translated={data.totals.rules_translated}
              />
              <TotalsBar
                testId="totals-errors"
                label="Page-build error codes" // i18n-ignore
                total={data.totals.errors_total}
                translated={data.totals.errors_translated}
              />
              {!data.translator_read_ok && (
                <div
                  data-testid="translator-read-warn"
                  className="text-xs text-amber-700 dark:text-amber-300 mt-2"
                >{/* i18n-ignore: developer-only page */}
                  Could not read the rule-translation file. Every rule shows
                  as &ldquo;not yet translated&rdquo; until the file is restored.
                </div>
              )}
            </div>

            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50 mb-2 mt-4">
              {/* i18n-ignore */}Reconciliation rules ({data.reconciliation_rules.length})
            </h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 mb-8">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900 text-left">
                  <tr className="text-slate-600 dark:text-slate-300">
                    <th className="px-3 py-2 font-medium">Country{/* i18n-ignore */}</th>
                    <th className="px-3 py-2 font-medium">Form{/* i18n-ignore */}</th>
                    <th className="px-3 py-2 font-medium">Rule name{/* i18n-ignore */}</th>
                    <th className="px-3 py-2 font-medium">Backend description{/* i18n-ignore */}</th>
                    <th className="px-3 py-2 font-medium">Status{/* i18n-ignore */}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.reconciliation_rules.map((r, i) => (
                    <tr
                      key={`${r.country}-${r.name}`}
                      data-testid={`rule-row-${r.name}`}
                      className={`border-t border-slate-100 dark:border-slate-800 ${
                        r.has_translation
                          ? ""
                          : "bg-rose-50/40 dark:bg-rose-950/20"
                      }`}
                    >
                      <td className="px-3 py-2 tabular-nums font-semibold">{r.country}</td>
                      <td className="px-3 py-2 tabular-nums text-slate-500 dark:text-slate-400">{r.form}</td>
                      <td className="px-3 py-2 tabular-nums">{r.name}</td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{r.description}</td>
                      <td className="px-3 py-2">
                        <Badge ok={r.has_translation} testId={`rule-badge-${r.name}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-50 mb-2 mt-4">
              {/* i18n-ignore */}Page-build error codes ({data.page_build_errors.length})
            </h2>
            <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 mb-8">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-900 text-left">
                  <tr className="text-slate-600 dark:text-slate-300">
                    <th className="px-3 py-2 font-medium">Error code{/* i18n-ignore */}</th>
                    <th className="px-3 py-2 font-medium">Status{/* i18n-ignore */}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.page_build_errors.map((e) => (
                    <tr
                      key={e.code}
                      data-testid={`page-build-error-row-${e.code}`}
                      className={`border-t border-slate-100 dark:border-slate-800 ${
                        e.has_translation
                          ? ""
                          : "bg-rose-50/40 dark:bg-rose-950/20"
                      }`}
                    >
                      <td className="px-3 py-2 tabular-nums">{e.code}</td>
                      <td className="px-3 py-2">
                        <Badge ok={e.has_translation} testId={`page-build-error-badge-${e.code}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6">
              <button
                type="button"
                data-testid="rule-copy-coverage-back"
                onClick={() => nav("/")}
                className="text-xs px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-700 dark:hover:text-teal-300 text-slate-700 dark:text-slate-200"
              >
                {/* i18n-ignore */}← Back to dashboard
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
